import { useQuery } from "@tanstack/react-query"
import axios from "axios"
import specialCases from "config/special"
import { filterSuppressedFaculty } from "services/emma/lib"
import { inflateProgramFeed } from "services/program/projectFeed"
import { addMinutes, getDate } from "date-fns"
import groupBy from "lodash/groupBy"
import { emmaFetch } from "services/emma/server"

// Browser requests go through the server-side proxy (pages/api/emma/[...path].js), which
// attaches the Emma bearer token. Paths below are Emma paths, unchanged.
const emmaApiUrl = "/api/emma"

export function useConference(meetingId, config, enabled = true) {
    const defaultConfig = {
        queryKey: ["conference", meetingId],
    }

    config = { ...defaultConfig, ...config }

    return useQuery({
        queryKey: config.queryKey,
        queryFn: async () => {
            const { data } = await axios.get(emmaApiUrl + "/conferences/" + meetingId)

            return data.data
        },
        refetchOnWindowFocus: false,
        enabled: enabled,
    })
}

export function useConferenceEvents(meetingId, params, config, enabled = true) {
    const defaultParams = {
        depth: "all",
        filter_status: "Published",
        limit: 0,
    }

    params = { ...defaultParams, ...params }

    const defaultConfig = {
        queryKey: ["events"],
        videoLinksLevel: 2,
        withVideoOnly: false,
        onDemandOnly: false,
        excludeOnDemand: false,
        removePrivateAndNotOnDemandSessions: false,
        prepEventListForChannel: false,
        sessions: false,
        proxy: false,
        // Use the cached, slimmed program feed (/api/program/:meetingId). Ignores `params`;
        // only for consumers that need the whole published program (program guide).
        programFeed: false,
    }

    config = { ...defaultConfig, ...config }

    const endpoint = config.sessions ? "sessions" : "events"

    return useQuery({
        queryKey: config.queryKey,
        queryFn: async () => {
            let events

            if (config.programFeed && !config.sessions) {
                const { data } = await axios.get(`/api/program/${meetingId}`)
                events = inflateProgramFeed(data)
            } else {
                // TODO: how to get the meetingId here without passing it
                const url =
                    config.proxy && !config.sessions
                        ? `/api/emma/events/${meetingId}`
                        : emmaApiUrl + "/conferences/" + meetingId + "/" + endpoint
                const { data } = await axios.get(url, {
                    params,
                })

                events = data?.events?.data
            }

            if (!events?.length) {
                return false // no events found
            }

            // only include ondemand events
            if (config.onDemandOnly) {
                events = events.filter(event => {
                    return event.params.on_demand
                })
            }

            // exclude ondemand events
            if (config.excludeOnDemand) {
                events = events.filter(event => {
                    return !event.params.on_demand
                })
            }

            // only include events with brightcove video links
            if (config.withVideoOnly) {
                events = events.filter(event => {
                    return event.links?.length
                })
            }

            // filter out events that are Private and aren't On-demand
            if (config.removePrivateAndNotOnDemandSessions) {
                events = events.filter(event => {
                    return !(event.status == "Private" && !event.params.on_demand)
                })
            }

            filterSuppressedFacultyFromEvents(meetingId, events)

            // filter out suppressed events
            if (specialCases[meetingId]?.suppressEvents) {
                events = events.filter(event => {
                    return !specialCases[meetingId].suppressEvents.includes(event.id)
                })
            }

            // order events by date, /sessions endpoint does not return events in order
            events = events.sort((a, b) => {
                return a.datetime_start < b.datetime_start ? -1 : 1
            })

            // prep for channels pages
            if (config.prepEventListForChannel) {
                // flatten events to the section level
                events = events.flatMap(event => {
                    return event.children
                })

                // if videos are at the presentation level, then flatten to the presentation level
                if (config.videoLinksLevel === 3) {
                    events = events.flatMap(event => {
                        // pass section tracks to the presentation level
                        event.children.forEach(
                            childEvent => (childEvent.tracks = childEvent.tracks.concat(event.tracks)),
                        )
                        return event.children
                    })
                }

                // filter out events with title "break" or "check-in"
                events = events.filter(event => {
                    return !["break", "check-in"].includes(event.title.toLowerCase())
                })

                // flattening and removing events can cause them to get out of order, re-establish order
                events = events.sort((a, b) => {
                    return a.datetime_start < b.datetime_start ? -1 : 1
                })

                events.forEach(event => {
                    // raise brightcove fields to event object for convenience
                    const videoLink = event.links?.find(link => link.type === "video" && link.id)
                    if (videoLink) {
                        event.preview_image_url = videoLink.preview_image_url
                        event.brightcove_id = videoLink.id
                    }
                })

                // Add datetime_end_buffer to last item in each session type of each day for live-streaming purposes
                const sessionTypes = groupBy(events, event => event.session_type)

                for (const [key, sessionType] of Object.entries(sessionTypes)) {
                    const days = groupBy(sessionType, event => {
                        return getDate(new Date(event.datetime_start))
                    })
                    for (const [key, day] of Object.entries(days)) {
                        const lastEvent = day[day.length - 1]
                        lastEvent.datetime_end_buffer = addMinutes(new Date(lastEvent.datetime_end), 30)
                    }
                }
            }

            return events
        },
        refetchOnWindowFocus: false,
        enabled: enabled,
    })
}

export async function getConferenceEvents(meetingId, params = {}, config = {}) {
    const defaultParams = {
        depth: "all",
        filter_status: "Published",
        limit: 0,
    }

    params = { ...defaultParams, ...params }

    const defaultConfig = {
        withVideoOnly: false,
        onDemandOnly: false,
        excludeOnDemand: false,
        removePrivateAndNotOnDemandSessions: false,
        revalidate: 300,
    }

    config = { ...defaultConfig, ...config }

    const searchParams = new URLSearchParams(params).toString()
    const response = await emmaFetch(`/conferences/${meetingId}/events?${searchParams}`, {
        next: { revalidate: config.revalidate },
    })
    const data = await response.json()

    let events = data?.events?.data

    if (!events?.length) {
        return []
    }

    if (config.onDemandOnly) {
        events = events.filter(event => event.params.on_demand)
    }

    if (config.excludeOnDemand) {
        events = events.filter(event => !event.params.on_demand)
    }

    if (config.withVideoOnly) {
        events = events.filter(event => event.links?.length)
    }

    if (config.removePrivateAndNotOnDemandSessions) {
        events = events.filter(event => !(event.status == "Private" && !event.params.on_demand))
    }

    filterSuppressedFacultyFromEvents(meetingId, events)

    if (specialCases[meetingId]?.suppressEvents) {
        events = events.filter(event => !specialCases[meetingId].suppressEvents.includes(event.id))
    }

    events = events.sort((a, b) => (a.datetime_start < b.datetime_start ? -1 : 1))

    return events
}

export function useConferenceEvent(meetingId, eventId, params, config) {
    const defaultParams = {
        depth: "all",
    }

    params = { ...defaultParams, ...params }

    const defaultConfig = {
        queryKey: ["event", eventId],
        withVideoOnly: true,
    }

    config = { ...defaultConfig, ...config }

    return useQuery({
        queryKey: config.queryKey,
        queryFn: async () => {
            const { data } = await axios.get(emmaApiUrl + "/conferences/" + meetingId + "/events/" + eventId, {
                params,
            })

            filterSuppressedFacultyFromEvents(meetingId, [data.event])

            return data.event
        },
        refetchOnWindowFocus: false,
    })
}

export function useConferenceFaculty(meetingId, filterRoles = ["faculty"], enabled = true) {
    return useQuery({
        queryKey: ["faculty"],
        queryFn: async () => {
            const { data } = await axios.get(emmaApiUrl + "/conferences/" + meetingId + "/faculty", {
                params: {
                    limit: 0,
                    filter_role: filterRoles,
                },
            })

            return filterSuppressedFaculty(meetingId, data.faculty.data)
        },
        refetchOnWindowFocus: false,
        enabled: enabled,
    })
}

export function useConferenceFacultyProfile(meetingId, facultyId, enabled = true) {
    return useQuery({
        queryKey: ["faculty", facultyId],
        queryFn: async () => {
            const { data } = await axios.get(emmaApiUrl + "/conferences/" + meetingId + "/faculty/" + facultyId)

            return data.faculty
        },
        refetchOnWindowFocus: false,
        enabled: enabled,
    })
}

// https://agenda.crf.org/api/v1/conferences/179/special_faculty
export function useConferenceSpecialFaculty(meetingId, enabled = true) {
    return useQuery({
        queryKey: ["specialFaculty"],
        queryFn: async () => {
            const { data } = await axios.get(emmaApiUrl + "/conferences/" + meetingId + "/special_faculty")

            const specialFaculty = data.faculty.data.filter(faculty => {
                return faculty.role !== "Program Planning Group"
            })

            return filterSuppressedFaculty(meetingId, specialFaculty)
        },
        refetchOnWindowFocus: false,
        enabled: enabled,
    })
}

// https://agenda.crf.org/api/v1/conferences/176/categories/moderator-roles
export function useConferenceModeratorRoles(meetingId) {
    return useQuery({
        queryKey: ["moderatorRoles"],
        queryFn: async () => {
            const { data } = await axios.get(emmaApiUrl + "/conferences/" + meetingId + "/categories/moderator-roles")

            return data.data.filter(role => role.name !== "Program Planning Group")
        },
        refetchOnWindowFocus: false,
    })
}

// https://agenda.crf.org/api/v1/conferences/226/categories/meeting-planner-roles
export function useConferencePlannerRoles(meetingId) {
    return useQuery({
        queryKey: ["plannnerRoles"],
        queryFn: async () => {
            const { data } = await axios.get(
                emmaApiUrl + "/conferences/" + meetingId + "/categories/meeting-planner-roles",
            )

            return data.data.filter(role => role.name !== "Program Planning Group")
        },
        refetchOnWindowFocus: false,
    })
}

// https://agenda.crf.org/api/v1/conferences/191/categories/session-type
export function useSessionTypes(meetingId) {
    return useQuery({
        queryKey: ["sessionTypes"],
        queryFn: async () => {
            const { data } = await axios.get(emmaApiUrl + "/conferences/" + meetingId + "/categories/session-type")

            return data.data
        },
        refetchOnWindowFocus: false,
    })
}

// https://agenda.crf.org/api/v1/conferences/226/categories/tracks
export function useTracks(meetingId) {
    return useQuery({
        queryKey: ["tracks", meetingId],
        queryFn: async () => {
            const { data } = await axios.get(
                emmaApiUrl + "/conferences/" + meetingId + "/categories/tracks?in_use=true",
            )

            return data.data
        },
        refetchOnWindowFocus: false,
    })
}

export function useVenues(meetingId) {
    return useQuery({
        queryKey: ["venues", meetingId],
        queryFn: async () => {
            const { data } = await axios.get(emmaApiUrl + "/conferences/" + meetingId + "/venues?in_use=1")

            return data?.venues?.data
        },
        refetchOnWindowFocus: false,
    })
}

export function useGroups(type, meetingId) {
    const path =
        type === "bySessionType"
            ? `/conferences/${meetingId}/categories/session-type`
            : `/conferences/${meetingId}/categories/tracks?in_use=true`
    return useQuery({
        queryKey: ["emmaGroups", type, meetingId],
        queryFn: async () => {
            const { data } = await axios.get(emmaApiUrl + path)

            return data.data
        },
        refetchOnWindowFocus: false,
    })
}

export function useConferenceFacultyById(meetingId, facultyId) {
    return useQuery({
        queryKey: ["faculty", facultyId],
        queryFn: async () => {
            const { data } = await axios.get(emmaApiUrl + "/conferences/" + meetingId + "/faculty" + facultyId)

            return data.event.data
        },
        refetchOnWindowFocus: false,
    })
}

function filterSuppressedFacultyFromEvents(meetingId, events) {
    events.forEach(event => {
        if (event.moderators?.length) {
            event.moderators.forEach(group => {
                group.users = filterSuppressedFaculty(meetingId, group.users, "id")
            })
        }
        if (event.children?.length) {
            filterSuppressedFacultyFromEvents(meetingId, event.children)
        }
    })
}
